/**
 * The host of the show.
 *
 * Hand-authored SVG rather than generated art: it stays crisp at any size,
 * takes its colours from the theme tokens, weighs nothing, and — the reason
 * that matters most — its parts can be animated per game state. A raster
 * spritesheet can only ever cut between poses.
 *
 * `state` is wired to real game state, never idle decoration (spec §10a).
 */
export type MascotState = "idle" | "listening" | "counting" | "cheering";

export function Mascot({
  state = "idle",
  className = "",
}: {
  state?: MascotState;
  className?: string;
}) {
  const eyeOpen = state !== "cheering";

  return (
    <svg
      viewBox="0 0 200 200"
      className={className}
      role="img"
      aria-label="Попугай — ведущий Parroty"
    >
      {/* tail */}
      <path
        d="M62 128c-16 10-30 26-34 44 16-2 34-10 46-22"
        fill="var(--color-sky)"
        stroke="#12162c"
        strokeWidth="6"
        strokeLinejoin="round"
      />

      {/* body */}
      <path
        d="M100 58c30 0 52 24 52 54 0 26-22 46-52 46s-52-20-52-46c0-30 22-54 52-54z"
        fill="var(--color-parrot)"
        stroke="#12162c"
        strokeWidth="6"
        strokeLinejoin="round"
      />

      {/* wing — lifts when cheering */}
      <path
        d="M132 96c14 4 22 18 20 34-2 14-12 24-24 26-6-20-4-42 4-60z"
        fill="var(--color-parrot-deep)"
        stroke="#12162c"
        strokeWidth="6"
        strokeLinejoin="round"
        style={{
          transformOrigin: "132px 100px",
          transform: state === "cheering" ? "rotate(-26deg)" : "rotate(0deg)",
          transition: "transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
      />

      {/* crest */}
      <path
        d="M86 44c-2-14 6-26 18-28-4 10-2 18 4 24"
        fill="var(--color-sun)"
        stroke="#12162c"
        strokeWidth="6"
        strokeLinejoin="round"
      />
      <path
        d="M104 40c4-14 16-22 28-20-8 8-12 18-10 28"
        fill="var(--color-onair)"
        stroke="#12162c"
        strokeWidth="6"
        strokeLinejoin="round"
      />

      {/* face patch */}
      <ellipse
        cx="92"
        cy="88"
        rx="34"
        ry="31"
        fill="var(--color-parrot-light)"
        stroke="#12162c"
        strokeWidth="6"
      />

      {/* eye */}
      {eyeOpen ? (
        <g className="mascot-eye">
          <circle cx="86" cy="84" r="11" fill="#fdfdff" stroke="#12162c" strokeWidth="5" />
          <circle
            cx={state === "listening" ? "89" : "86"}
            cy="85"
            r="5"
            fill="#12162c"
            style={{ transition: "cx 200ms ease" }}
          />
        </g>
      ) : (
        <path
          d="M75 86c6-7 16-7 22 0"
          fill="none"
          stroke="#12162c"
          strokeWidth="5"
          strokeLinecap="round"
        />
      )}

      {/* beak */}
      <path
        d="M60 92c-14 2-22 12-20 24 10 2 20-4 26-14z"
        fill="var(--color-amber)"
        stroke="#12162c"
        strokeWidth="6"
        strokeLinejoin="round"
      />

      {/* headset — this is a studio */}
      <path
        d="M56 70c8-20 30-32 52-28"
        fill="none"
        stroke="#12162c"
        strokeWidth="7"
        strokeLinecap="round"
      />
      <rect
        x="120"
        y="52"
        width="20"
        height="26"
        rx="8"
        fill="var(--color-berry)"
        stroke="#12162c"
        strokeWidth="6"
      />

      {/* feet */}
      <path
        d="M84 158v14M116 158v14"
        stroke="#12162c"
        strokeWidth="7"
        strokeLinecap="round"
      />

    </svg>
  );
}
